import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../../api/axios";
import { useAuth } from "../../AuthContext";
import { Mail, User, ShieldCheck, Camera } from "lucide-react";

export default function Profile() {
  const { user: contextUser, setUser, loading: authLoading, logout } = useAuth();
  const [user, setLocalUser] = useState(null);
  const [uploading, setUploading] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (authLoading) return;
    if (!contextUser) {
      navigate("/login", { replace: true });
      return;
    }

    setLocalUser(contextUser);

    const token = localStorage.getItem("token");
    if (token) {
      api.get("/user", {
        headers: { Authorization: `Bearer ${token}` },
      })
        .then((res) => {
          const apiUser = res.data.user || res.data.data?.user;
          if (apiUser) {
            setLocalUser(apiUser);
            setUser(apiUser);
          }
        })
        .catch(() => {
          logout();
          navigate("/login", { replace: true });
        });
    }
  }, [contextUser, authLoading, navigate, logout, setUser]);

  const handleImageUpload = async (e) => {
    if (!user) return;
    const file = e.target.files[0];
    if (!file) return;

    setUploading(true);
    const formData = new FormData();
    formData.append("profile_image", file);

    try {
      const token = localStorage.getItem("token");
      const res = await api.post("/profile/upload", formData, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      const updatedUser = { ...user, profile_image: res.data.profile_image };
      setLocalUser(updatedUser);
      setUser(updatedUser);
    } finally {
      setUploading(false);
    }
  };

  if (!user) return <p className="text-center mt-10">جارٍ التحميل...</p>;

  return (
    <div className="min-h-screen bg-slate-50 flex justify-center py-12">
      <div className="w-full max-w-md bg-white border border-gray-200 rounded-xl shadow-sm p-6">

        {/* Avatar */}
        <div className="flex flex-col items-center mb-6 relative">
          {user.profile_image ? (
            <img
              src={user.profile_image}
              className="w-28 h-28 rounded-full border border-green-500 object-cover"
              alt="avatar"
            />
          ) : (
            <div className="w-28 h-28 rounded-full bg-green-100 flex items-center justify-center text-3xl text-green-700">
              {user.name?.charAt(0) || "U"}
            </div>
          )}

          <label className="absolute bottom-1 right-[38%] bg-white border border-gray-300 p-2 rounded-full cursor-pointer">
            <Camera size={16} className="text-green-600" />
            <input type="file" hidden accept="image/*" onChange={handleImageUpload} />
          </label>

          {uploading && (
            <p className="text-sm text-gray-500 mt-2">جاري رفع الصورة...</p>
          )}
        </div>

        {/* Name */}
        <h2 className="text-center text-lg font-semibold text-gray-800">
          {user.name || "لا يوجد اسم"}
        </h2>
        <p className="text-center text-sm text-gray-500 mb-6">
          {user.email || "لا يوجد بريد إلكتروني"}
        </p>

        {/* Info */}
        <div className="space-y-3">
          <InfoRow icon={Mail} label="البريد الإلكتروني" value={user.email || "-"} />
          <InfoRow icon={User} label="الدور" value={user.role || "-"} />
          {user.role === "user" && (
            <>
              <InfoRow icon={User} label="الموقع" value={user.address_googel_map || "-"} />
              <InfoRow icon={User} label="نوع العمل" value={user.type_work || "-"} />
            </>
          )}
          <InfoRow icon={ShieldCheck} label="الحالة" value={user.account_status || "-"} />
        </div>

        {/* Logout */}
        <button
          onClick={logout}
          className="mt-6 w-full py-2 rounded-md bg-green-600 text-white hover:bg-green-700 transition"
        >
          تسجيل الخروج
        </button>
      </div>
    </div>
  );
}

function InfoRow({ icon: Icon, label, value }) {
  return (
    <div className="flex items-center gap-3 border border-gray-200 rounded-md p-3">
      <Icon size={18} className="text-green-600" />
      <div>
        <p className="text-xs text-gray-500">{label}</p>
        <p className="text-sm font-medium text-gray-800">{value}</p>
      </div>
    </div>
  );
}
